import { type ComponentType, type PropsWithChildren } from 'react';
import { type JSX } from 'react/jsx-runtime';

import { type MessageCategory } from '../types';

type ChoreographerWrapperProps = {
	messageId: string;
	messageTemplateId: string;
	messageCategory?: MessageCategory;
};

/**
 * Conditionally wraps `children` in the supplied `ChoreographerWrapper` component when `enabled`
 * is true (typically: non client-triggered messages), otherwise renders `children` unchanged.
 * Client-triggered messages skip the outer choreographer because they handle their own
 * choreography internally via `waitForTrigger`.
 *
 * The choreographer wrapper is passed in via the `ChoreographerWrapper` prop (rather than
 * imported here) so the V1 and V2 message renderers each continue using their own sibling
 * `ChoreographerWrapper`. This keeps each package's `jest.spyOn(<own module>,
 * 'ChoreographerWrapper')` working unchanged, avoids any cross-package call routing that would
 * be invisible at the renderer's call site, and isolates this file to the only piece of logic
 * that is genuinely shared — the "wrap when enabled, passthrough otherwise" branch.
 */
export const MaybeChoreographerWrapper = ({
	ChoreographerWrapper,
	enabled,
	messageId,
	messageTemplateId,
	messageCategory,
	children,
}: PropsWithChildren<
	{
		ChoreographerWrapper: ComponentType<PropsWithChildren<ChoreographerWrapperProps>>;
		enabled: boolean;
	} & ChoreographerWrapperProps
>): JSX.Element => {
	if (!enabled) {
		return <>{children}</>;
	}
	return (
		<ChoreographerWrapper
			messageId={messageId}
			messageTemplateId={messageTemplateId}
			messageCategory={messageCategory}
		>
			{children}
		</ChoreographerWrapper>
	);
};
